import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <Link to="/landing" className="inline-flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 mb-8" data-testid="privacy-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: March 16, 2026</p>

        <div className="prose prose-gray prose-sm max-w-none space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p>
              All About Claims ("we," "our," or "us") operates the claims management platform accessible at this website (the "Service"). This Privacy Policy describes how we collect, use, and protect your personal information when you use our Service.
            </p>
            <p>
              By accessing or using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
            <h3 className="text-sm font-semibold text-gray-800 mt-4 mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account Information:</strong> Name, email address, company name, and password when you register.</li>
              <li><strong>Claims Data:</strong> Information you enter into claims intake forms, including claimant details, loss descriptions, policy numbers, and supporting documents.</li>
              <li><strong>Payment Information:</strong> Billing details processed through our third-party payment processor (Stripe). We do not store your full credit card number on our servers.</li>
              <li><strong>Communications:</strong> Any messages or inquiries you send to us.</li>
            </ul>

            <h3 className="text-sm font-semibold text-gray-800 mt-4 mb-2">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Usage Data:</strong> Pages visited, features used, timestamps, and interaction patterns.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers.</li>
              <li><strong>IP Address:</strong> Used for approximate geolocation and security purposes.</li>
              <li><strong>Cookies:</strong> Session cookies to maintain your login state and preferences.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide, maintain, and improve the Service.</li>
              <li>Process and manage your claims data.</li>
              <li>Authenticate your identity and manage your account.</li>
              <li>Process payments and manage subscriptions.</li>
              <li>Send transactional emails (e.g., account verification, trial reminders, team invitations).</li>
              <li>Monitor usage to detect and prevent fraud or abuse.</li>
              <li>Respond to your inquiries and provide customer support.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">4. How We Share Your Information</h2>
            <p>We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Service Providers:</strong> Third-party vendors that help us operate the Service (e.g., Stripe for payments, Resend for email delivery, MongoDB for data storage). These providers only access data necessary to perform their services.</li>
              <li><strong>Within Your Organization:</strong> Other users within your company or organization account may access shared claims data as permitted by your account roles.</li>
              <li><strong>Legal Requirements:</strong> If required by law, subpoena, or other legal process, or to protect the rights, property, or safety of All About Claims, our users, or the public.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">5. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information, including encrypted data transmission (HTTPS/TLS), secure password hashing, role-based access controls, and regular security reviews. However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to provide the Service. Claims data is retained in accordance with your subscription plan. If you delete your account, we will remove your personal information within 30 days, except where retention is required by law or for legitimate business purposes.
            </p>
            <p>
              Deleted claims and documents are moved to a Recycle Bin and can be recovered within 30 days before permanent deletion.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal retention requirements.</li>
              <li><strong>Portability:</strong> Request an export of your data in a machine-readable format (CSV/PDF).</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from non-essential communications at any time.</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, contact us at <a href="mailto:privacy@allaboutclaims.us" className="text-teal-600 hover:underline">privacy@allaboutclaims.us</a>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">8. Cookies</h2>
            <p>
              We use essential cookies to maintain your session and authenticate your identity. These cookies are strictly necessary for the Service to function and cannot be disabled. We do not use advertising or third-party tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">9. Third-Party Services</h2>
            <p>Our Service integrates with the following third-party providers:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Stripe:</strong> Payment processing. Subject to <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Stripe's Privacy Policy</a>.</li>
              <li><strong>Resend:</strong> Transactional email delivery. Subject to <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Resend's Privacy Policy</a>.</li>
              <li><strong>Google OAuth:</strong> Optional social login. Subject to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Google's Privacy Policy</a>.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">10. Children's Privacy</h2>
            <p>
              The Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will take steps to delete it.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page and updating the "Last updated" date. Your continued use of the Service after any changes constitutes acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">12. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy, please contact us at:</p>
            <div className="bg-gray-50 rounded-lg p-4 mt-3 text-sm">
              <p className="font-semibold text-gray-900">All About Claims</p>
              <p>Illinois, United States</p>
              <p>Email: <a href="mailto:privacy@allaboutclaims.us" className="text-teal-600 hover:underline">privacy@allaboutclaims.us</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
